import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { useState } from "react";

import { Button } from "@fronterahealth/frontera-ui-components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@fronterahealth/frontera-ui-components/ui/dropdown-menu";

import { ReportSectionsType } from "@api/graphql/types-and-hooks";
import { ReportSectionActionMenuMoveToAppendix } from "@components/ReportField/ReportSection/ReportSectionActionMenu/ReportSectionActionMenuMoveToAppendix";
import {
  ReportSectionActionMenuRegenerateDialog,
  ReportSectionActionMenuRegenerateItem,
} from "@components/ReportField/ReportSection/ReportSectionActionMenu/ReportSectionActionMenuRegenerate";
import {
  ReportSectionActionMenuRemoveDialog,
  ReportSectionActionMenuRemoveItem,
} from "@components/ReportField/ReportSection/ReportSectionActionMenu/ReportSectionActionMenuRemove";
import { ReportSectionActionMenuReorder } from "@components/ReportField/ReportSection/ReportSectionActionMenu/ReportSectionActionMenuReorder";
import { ReportSectionActionMenuReturnFromAppendix } from "@components/ReportField/ReportSection/ReportSectionActionMenu/ReportSectionActionMenuReturnFromAppendix";
import {
  useCanDeleteSection,
  useCanMoveSectionToAppendix,
  useCanRegenerateSection,
  useCanReorderSection,
  useCanReturnSectionFromAppendix,
} from "@components/ReportField/capabilities";

export interface ReportSectionActionMenuProps {
  section: ReportSectionsType;
}

export const ReportSectionActionMenu: React.FC<ReportSectionActionMenuProps> = ({ section }) => {
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isRegenerateOpen, setIsRegenerateOpen] = useState(false);

  const canRegenerate = useCanRegenerateSection(section);
  const canReorder = useCanReorderSection(section);
  const canMoveToAppendix = useCanMoveSectionToAppendix(section);
  const canReturnFromAppendix = useCanReturnSectionFromAppendix(section);
  const canDelete = useCanDeleteSection(section);

  const hasItemsAboveRemove = canRegenerate || canReorder || canMoveToAppendix || canReturnFromAppendix;
  const showActionMenu = hasItemsAboveRemove || canDelete;

  if (!showActionMenu) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button variant="ghost" size="icon" className="rounded-full" aria-label="Section actions">
              <EllipsisVerticalIcon className="size-6" />
            </Button>
          }
        />
        <DropdownMenuContent className="min-w-48" align="end">
          <DropdownMenuGroup>
            {canRegenerate && (
              <ReportSectionActionMenuRegenerateItem section={section} onOpen={() => setIsRegenerateOpen(true)} />
            )}
            {canReorder && <ReportSectionActionMenuReorder section={section} />}
            {canMoveToAppendix && <ReportSectionActionMenuMoveToAppendix section={section} />}
            {canReturnFromAppendix && <ReportSectionActionMenuReturnFromAppendix section={section} />}
          </DropdownMenuGroup>
          {canDelete && (
            <>
              {hasItemsAboveRemove && <DropdownMenuSeparator />}
              <ReportSectionActionMenuRemoveItem onOpen={() => setIsDeleteOpen(true)} />
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {canRegenerate && (
        <ReportSectionActionMenuRegenerateDialog
          section={section}
          open={isRegenerateOpen}
          onOpenChange={setIsRegenerateOpen}
        />
      )}
      {canDelete && (
        <ReportSectionActionMenuRemoveDialog section={section} open={isDeleteOpen} onOpenChange={setIsDeleteOpen} />
      )}
    </>
  );
};
